import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OpsInfrastructureState } from '../../../../libs/api-client/src/generated/models';
import { Icon } from '../components/Brand';
import { PageBody, PageHead } from '../components/Page';
import { Pill } from '../components/Pill';
import { StatusBanner } from '../components/StatusBanner';
import { DatabasePanel } from '../components/infrastructure/DatabasePanel';
import { LevelBadge } from '../components/infrastructure/InfrastructureParts';
import { LoadBalancerPanel } from '../components/infrastructure/LoadBalancerPanel';
import { NodeCard } from '../components/infrastructure/NodeCard';
import { NodeTable } from '../components/infrastructure/NodeTable';
import { RedisPanel } from '../components/infrastructure/RedisPanel';
import { intlLocale } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';
import {
  commonNodePrefix,
  formatBytes,
  formatNumber,
  formatPercent,
  INFRASTRUCTURE_POLL_MS,
  liveCount,
  pageStateLabelKey,
  sourceStateLabelKey,
  sourceStateTone,
} from '../lib/infrastructure';
import {
  fetchInfrastructure,
  INFRASTRUCTURE_QUERY_KEY,
  type OpsInfrastructureLevel,
  type OpsInfrastructureSnapshot,
} from '../lib/infrastructure-api';

/** One headline figure, in the operations dashboard's KPI-tile shape. */
function Tile({
  label,
  value,
  level,
  sub,
}: {
  readonly label: string;
  readonly value: string;
  readonly level?: OpsInfrastructureLevel;
  readonly sub?: string;
}) {
  return (
    <div className="surface p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      <div className="mt-1 flex items-center gap-2 text-xs">
        {level && <LevelBadge level={level} />}
        {sub && <span className="truncate text-gray-500">{sub}</span>}
      </div>
    </div>
  );
}

/** The four headline figures above the node grid. */
function Summary({
  snapshot,
}: {
  readonly snapshot: OpsInfrastructureSnapshot;
}) {
  const { t, locale } = useLocale();
  const { live, total } = liveCount(snapshot.nodes);
  const database = snapshot.database;
  const redis = snapshot.redis;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Tile
        label={t('infrastructure.summary.nodes')}
        value={t('infrastructure.summary.nodesValue', {
          live: formatNumber(live, locale),
          total: formatNumber(total, locale),
        })}
      />
      <Tile
        label={t('infrastructure.summary.connections')}
        value={
          database
            ? `${formatNumber(database.open, locale)} / ${formatNumber(database.usable, locale)}`
            : '—'
        }
        level={database?.openLevel}
      />
      <Tile
        label={t('infrastructure.summary.ceiling')}
        value={database ? formatPercent(database.ceilingPercent, locale) : '—'}
        level={database?.ceilingLevel}
      />
      <Tile
        label={t('infrastructure.summary.redis')}
        value={redis ? formatBytes(redis.usedBytes, locale) : '—'}
        level={redis?.level}
      />
    </div>
  );
}

/**
 * Infrastruktur (work order NODE-MONITORING-DASHBOARD): every server's load, the Load Balancer and
 * the database's connection budget, built from the dashboard's own parts — `PageHead`, `.surface`
 * panels, `Pill`/`Badge`, `.data-table` — so it reads as one more page of the ops tool.
 *
 * Polls the stored snapshot every 15 s (the collector's own pace) and only while the tab is
 * visible; a refetch keeps the previous render on screen rather than flashing a skeleton.
 */
export function InfrastructurePage() {
  const { t, locale } = useLocale();
  const [asTable, setAsTable] = useState(false);
  const { data, error, isFetching, refetch } = useQuery({
    queryKey: INFRASTRUCTURE_QUERY_KEY,
    queryFn: fetchInfrastructure,
    refetchInterval: INFRASTRUCTURE_POLL_MS,
    refetchIntervalInBackground: false,
  });

  const snapshot = data?.snapshot ?? null;
  const nodePrefix = commonNodePrefix(
    snapshot?.nodes.map((node) => node.name) ?? [],
  );
  const collectedAt = snapshot
    ? new Intl.DateTimeFormat(intlLocale(locale), {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(new Date(snapshot.collectedAt))
    : null;

  return (
    <>
      <PageHead
        eyebrow={t('infrastructure.eyebrow')}
        title={t('infrastructure.title')}
        lead={
          collectedAt
            ? `${t('infrastructure.lead')} ${t('infrastructure.collectedAt', { time: collectedAt })}`
            : t('infrastructure.lead')
        }
        actions={
          <>
            <button
              type="button"
              className="btn-neutral btn-sm"
              onClick={() => setAsTable((value) => !value)}
              aria-pressed={asTable}
            >
              {asTable
                ? t('infrastructure.table.hide')
                : t('infrastructure.table.show')}
            </button>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <Icon
                name="refresh"
                className={`mr-1.5 h-4 w-4 ${isFetching ? 'motion-safe:animate-spin' : ''}`}
              />
              {t('infrastructure.refresh')}
            </button>
          </>
        }
      >
        {snapshot && (
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'hetzner', state: snapshot.sources.hetzner },
              { key: 'database', state: snapshot.sources.database },
              { key: 'redis', state: snapshot.sources.redis },
            ].map(({ key, state }) => (
              <Pill key={key} tone={sourceStateTone(state)}>
                {t(`infrastructure.sources.${key}`)} ·{' '}
                {t(sourceStateLabelKey(state))}
              </Pill>
            ))}
          </div>
        )}
      </PageHead>

      <PageBody>
        {error && (
          <StatusBanner tone="error">
            {t('infrastructure.loadFailed', {
              message: error instanceof Error ? error.message : String(error),
            })}
          </StatusBanner>
        )}
        {data && data.state !== OpsInfrastructureState.Ok && (
          <StatusBanner
            tone={
              data.state === OpsInfrastructureState.Collecting
                ? 'loading'
                : 'warning'
            }
          >
            {t(pageStateLabelKey(data.state))}
          </StatusBanner>
        )}
        {!data && !error && (
          <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
        )}

        {snapshot && (
          <>
            <Summary snapshot={snapshot} />

            {snapshot.nodes.length === 0 ? (
              <div className="surface p-6 text-center">
                <p className="text-sm font-semibold text-gray-900">
                  {t('infrastructure.empty.title')}
                </p>
                <p className="mx-auto mt-1 max-w-xl text-sm text-gray-500">
                  {t('infrastructure.empty.body')}
                </p>
              </div>
            ) : asTable ? (
              <NodeTable nodes={snapshot.nodes} nodePrefix={nodePrefix} />
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {snapshot.nodes.map((node) => (
                  <NodeCard
                    key={node.name}
                    node={node}
                    nodePrefix={nodePrefix}
                  />
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
              <DatabasePanel
                database={snapshot.database}
                state={snapshot.sources.database}
                nodePrefix={nodePrefix}
              />
              <div className="flex min-w-0 flex-col gap-4">
                <RedisPanel redis={snapshot.redis} />
                <LoadBalancerPanel
                  loadBalancers={snapshot.loadBalancers}
                  state={snapshot.sources.hetzner}
                  nodePrefix={nodePrefix}
                />
              </div>
            </div>
          </>
        )}
      </PageBody>
    </>
  );
}
